import { createSlice, PayloadAction } from "@reduxjs/toolkit";

import { type RootState } from "@/data/store";
import { onContinueExam } from "@/features/assignment";
import { submit } from "@/features/authority";
import { SubmissionType } from "@/generated/graphql";

/** Materials redux state */
export interface LayoutState {
  /** Open state of the left Materials pane */
  isMaterialsOpen: boolean;
  /** Currently selected tab to render in the panes */
  materialsTab: MaterialsTab;
  /** Open editor track for the body editor on the right. */
  isTrackOpen: boolean;
  /** Open state of the right-hand version history panel. */
  isVersionHistoryOpen: boolean;
}

/**
 * Various selectable tabs to determine the contents of the Materials panes.
 * Materials sit adjacent to the main Student Work.
 */
export enum MaterialsTab {
  Instructions = "instructions",
  Notes = "notes",
  Draft = "draft",
  Final = "final",
  Files = "files",
}

const initialState: LayoutState = {
  isMaterialsOpen: true,
  isTrackOpen: false,
  isVersionHistoryOpen: false,
  materialsTab: MaterialsTab.Instructions,
};

/**
 * Bringing materials up ends replay. `usePanelVisibility` reports materials as
 * `isMaterialsOpen && !isVersionHistoryOpen`, so raising the flag while version
 * history is open produces a pair that renders as neither: materials asked for
 * and hidden, behind a replay the student never asked to stay in.
 *
 * The rule lives here rather than only in `openPanel` because the tab bar, a
 * finished submission and the continue-exam action all raise the flag
 * directly, without going anywhere near that hook.
 */
function showMaterials(state: LayoutState): void {
  state.isMaterialsOpen = true;
  state.isVersionHistoryOpen = false;
}

export const layoutSlice = createSlice({
  name: "layout",
  initialState,
  reducers: {
    setMaterialsTab: (state, action: PayloadAction<MaterialsTab>) => {
      state.materialsTab = action.payload;
      showMaterials(state);
    },
    openMaterials: (state) => {
      state.isMaterialsOpen = true;
    },
    closeMaterials: (state) => {
      state.isMaterialsOpen = false;
    },
    openTrack: (state) => {
      state.isTrackOpen = true;
    },
    closeTrack: (state) => {
      state.isTrackOpen = false;
    },
    openVersionHistory: (state) => {
      state.isVersionHistoryOpen = true;
    },
    closeVersionHistory: (state) => {
      state.isVersionHistoryOpen = false;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(submit.fulfilled, (state, action) => {
      showMaterials(state);
      if (action.meta.arg?.submissionType === SubmissionType.Draft) {
        state.materialsTab = MaterialsTab.Draft;
      } else {
        state.materialsTab = MaterialsTab.Final;
      }
    });

    builder.addCase(onContinueExam, (state) => {
      showMaterials(state);
      state.materialsTab = MaterialsTab.Instructions;
    });
  },
});

// Export Actions
export const {
  setMaterialsTab,
  openMaterials,
  closeMaterials,
  openTrack,
  closeTrack,
  openVersionHistory,
  closeVersionHistory,
} = layoutSlice.actions;

// Export Reducer
export const layoutReducer = layoutSlice.reducer;

// Selectors
export function selectMaterialsOpen(state: RootState) {
  return state.layout.isMaterialsOpen;
}

export function selectMaterialsTab(state: RootState) {
  return state.layout.materialsTab;
}

export function selectLayoutState(state: RootState) {
  return state.layout;
}

export function selectTrackOpen(state: RootState) {
  return state.layout.isTrackOpen;
}

export function selectVersionHistoryOpen(state: RootState) {
  return state.layout.isVersionHistoryOpen;
}
